import { Account, AccountBalanceChart } from '@monorepo/types';
import { useStore } from '@hub/helpers/use-store';
import { useQuery } from '@tanstack/react-query';
import { shouldDisablePolling } from '../../utils/polling-utils';
import {
  ACCOUNT_QUERY_KEYS,
  TRADES_POLLING_INTERVAL,
  TRADES_STALE_TIME,
} from '../constants';

// Type for API response that might wrap data in 'json' property
type BalanceChartResponse =
  | AccountBalanceChart[]
  | { json: AccountBalanceChart[] };

/**
 * Custom hook for fetching account performance chart data with real-time polling.
 *
 * Handles API responses that might wrap data in a 'json' property and provides
 * consistent AccountBalanceChart[] data for chart visualization. Automatically
 * polls for updates every 30 seconds to ensure chart data stays current with
 * backend position sync jobs.
 *
 * @param accountId - The trading system account ID to fetch chart data for
 * @returns React Query result object containing chart data, loading state, and error
 *
 * @example
 * ```tsx
 * const { data: chartData, isLoading, error, refetch } = usePerformanceChartData('TS-123');
 *
 * if (isLoading) return <ChartLoader />;
 * if (error) return <ErrorMessage onRetry={refetch} />;
 * if (!chartData?.length) return <NoDataMessage />;
 *
 * return <PerformanceChart data={chartData} />;
 * ```
 */
export const usePerformanceChartData = (
  accountId?: string,
  account?: Account,
) => {
  const {
    dataStore: { accountStore },
  } = useStore();

  const isPollingDisabled = shouldDisablePolling(account);

  return useQuery<AccountBalanceChart[]>({
    queryKey: ACCOUNT_QUERY_KEYS.PERFORMANCE_CHART(accountId || ''),
    queryFn: async () => {
      if (!accountId) throw new Error('Account ID is required');

      try {
        const result = (await accountStore.getBalanceChart(
          accountId,
        )) as BalanceChartResponse;

        // Handle the case where data is wrapped in a 'json' property
        const data = 'json' in result ? result.json : result;

        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('usePerformanceChartData: API error:', error);
        throw error;
      }
    },
    refetchInterval: isPollingDisabled ? false : TRADES_POLLING_INTERVAL, // Disable polling if account is disabled/paused
    refetchIntervalInBackground: false, // Don't poll when tab is not active
    staleTime: TRADES_STALE_TIME, // Data becomes stale after 25 seconds
    enabled: !!accountId,
  });
};
